import type { CodeType } from "@/clinical-codes/types/ClinicalCodeTypes";
import { useSearchIcd10Codes } from "@/clinical-codes/useSearchIcd10Codes";
import { useSearchOpcs4Codes } from "@/clinical-codes/useSearchOpcs4Codes";
import { MultiSelect } from "@/components/ui/multi-select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchBox, type SearchBoxRef } from "@/search/SearchBox";
import type { ClinicalCodeWithOrder } from "@/types/demo";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface ClinicalCodeSelectorProps {
  value: ClinicalCodeWithOrder[];
  onChange: (codes: ClinicalCodeWithOrder[]) => void;
  disabled?: boolean;
}

// Define CodeOption interface to match what will be used in MultiSelect
interface CodeOption {
  value: string;
  label: string;
  code: {
    code: string;
    description: string;
    type: "ICD-10" | "OPCS-4";
  };
}

export function ClinicalCodeSelector({ value, onChange, disabled = false }: ClinicalCodeSelectorProps) {
  const [activeTab, setActiveTab] = useState<CodeType>("ICD-10");
  const [icd10SearchTerm, setIcd10SearchTerm] = useState("");
  const [opcs4SearchTerm, setOpcs4SearchTerm] = useState("");
  const icd10SearchRef = useRef<SearchBoxRef>(null);
  const opcs4SearchRef = useRef<SearchBoxRef>(null);

  // Cache of all selected options to maintain selections when search changes
  const [selectedOptionsCache, setSelectedOptionsCache] = useState<Map<string, CodeOption>>(new Map());

  // Use separate hooks for each code type
  const {
    codes: icd10Codes = [],
    isLoading: isLoadingIcd10,
    fetchNextPage: fetchNextPageIcd10,
    hasNextPage: hasNextPageIcd10,
    getCodeByValue: getIcd10CodeByValue,
  } = useSearchIcd10Codes(icd10SearchTerm);

  const {
    codes: opcs4Codes = [],
    isLoading: isLoadingOpcs4,
    fetchNextPage: fetchNextPageOpcs4,
    hasNextPage: hasNextPageOpcs4,
    getCodeByValue: getOpcs4CodeByValue,
  } = useSearchOpcs4Codes(opcs4SearchTerm);

  // Initialize the cache with current selections
  useEffect(() => {
    const newCache = new Map<string, CodeOption>();

    // Update cache with current selections
    value.forEach((item) => {
      const key = `${item.type}-${item.code}`;
      if (!selectedOptionsCache.has(key)) {
        newCache.set(key, {
          value: key,
          label: `${item.code} - ${item.description}`,
          code: {
            code: item.code,
            description: item.description,
            type: item.type,
          },
        });
      } else {
        newCache.set(key, selectedOptionsCache.get(key)!);
      }
    });

    setSelectedOptionsCache(newCache);
  }, [value]);

  type MinimalCode = { code: string; description: string; type: CodeType };
  const getCodeOptions = (codes: MinimalCode[]): CodeOption[] => {
    return codes.map(
      (code): CodeOption => ({
        value: `${code.type}-${code.code}`,
        label: `${code.code} - ${code.description}`,
        code: {
          code: code.code,
          description: code.description,
          type: code.type,
        },
      }),
    );
  };

  // Add type property to codes for downstream compatibility
  const icd10Options = getCodeOptions(icd10Codes?.map((code) => ({ ...code, type: "ICD-10" as const })) ?? []);
  const opcs4Options = getCodeOptions(opcs4Codes?.map((code) => ({ ...code, type: "OPCS-4" as const })) ?? []);

  // Handle code selection - FIXED to preserve existing codes of the same type
  const handleCodeSelect = (selected: CodeOption[], type: CodeType) => {
    // Update the cache with all selected options
    const newCache = new Map(selectedOptionsCache);
    selected.forEach((option) => {
      newCache.set(`${option.code.type}-${option.code.code}`, option);
    });
    setSelectedOptionsCache(newCache);

    // Get currently selected codes of each type
    const icd10TypeCodes =
      type === "ICD-10"
        ? selected.map((option) => ({
            code: option.code.code,
            description: option.code.description,
            type: option.code.type as CodeType,
            order: 0, // Will be updated below
          }))
        : value.filter((c) => c.type === "ICD-10");

    const opcs4TypeCodes =
      type === "OPCS-4"
        ? selected.map((option) => ({
            code: option.code.code,
            description: option.code.description,
            type: option.code.type as CodeType,
            order: 0, // Will be updated below
          }))
        : value.filter((c) => c.type === "OPCS-4");

    // Combine both types of codes
    const allCodes = [...icd10TypeCodes, ...opcs4TypeCodes];

    // Update order numbers sequentially
    const updatedCodes = allCodes.map((item, index) => ({
      ...item,
      order: index,
    }));

    onChange(updatedCodes);
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(value);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order numbers
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    onChange(updatedItems);
  };

  // Focus search input when tab changes
  useEffect(() => {
    setTimeout(() => {
      if (activeTab === "ICD-10" && icd10SearchRef.current) {
        icd10SearchRef.current.focus();
      } else if (activeTab === "OPCS-4" && opcs4SearchRef.current) {
        opcs4SearchRef.current.focus();
      }
    }, 100);
  }, [activeTab]);

  // Get currently selected options based on type, ensuring all selected codes are shown
  // even if they're not in the current search results
  const getSelectedOptions = (type: CodeType, _options: CodeOption[]): CodeOption[] => {
    const result: CodeOption[] = [];

    // Add all selected codes for this type from the cache
    value.forEach((item) => {
      if (item.type === type) {
        const key = `${item.type}-${item.code}`;
        if (selectedOptionsCache.has(key)) {
          // Use the cached option if available
          result.push(selectedOptionsCache.get(key)!);
        } else {
          // Create a new option if not in cache
          const option: CodeOption = {
            value: key,
            label: `${item.code} - ${item.description}`,
            code: {
              code: item.code,
              description: item.description,
              type: item.type,
            },
          };
          result.push(option);
        }
      }
    });

    return result;
  };

  // Filter codes by type for display
  const icd10SelectedCodes = value.filter((code) => code.type === "ICD-10").sort((a, b) => a.order - b.order);

  const opcs4SelectedCodes = value.filter((code) => code.type === "OPCS-4").sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-4">
      <Tabs defaultValue="ICD-10" onValueChange={(value) => setActiveTab(value as CodeType)} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="ICD-10">ICD-10 Codes</TabsTrigger>
          <TabsTrigger value="OPCS-4">OPCS-4 Codes</TabsTrigger>
        </TabsList>

        <TabsContent value="ICD-10" className="space-y-4">
          <div className="relative">
            <SearchBox ref={icd10SearchRef} searchQuery={icd10SearchTerm} onSearchChange={setIcd10SearchTerm} />
            {isLoadingIcd10 && (
              <div className="absolute right-10 top-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          <MultiSelect
            options={icd10Options}
            value={getSelectedOptions("ICD-10", icd10Options)}
            onChange={(selected) => handleCodeSelect(selected as CodeOption[], "ICD-10")}
            placeholder="Select ICD-10 codes..."
            isLoading={isLoadingIcd10}
            disabled={disabled}
          />

          {icd10SearchTerm && icd10Options.length === 0 && !isLoadingIcd10 && (
            <div className="text-sm text-muted-foreground mt-2">
              No ICD-10 codes found matching "{icd10SearchTerm}". Try a different search term.
            </div>
          )}
        </TabsContent>

        <TabsContent value="OPCS-4" className="space-y-4">
          <div className="relative">
            <SearchBox ref={opcs4SearchRef} searchQuery={opcs4SearchTerm} onSearchChange={setOpcs4SearchTerm} />
            {isLoadingOpcs4 && (
              <div className="absolute right-10 top-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          <MultiSelect
            options={opcs4Options}
            value={getSelectedOptions("OPCS-4", opcs4Options)}
            onChange={(selected) => handleCodeSelect(selected as CodeOption[], "OPCS-4")}
            placeholder="Select OPCS-4 codes..."
            isLoading={isLoadingOpcs4}
            disabled={disabled}
          />

          {opcs4SearchTerm && opcs4Options.length === 0 && !isLoadingOpcs4 && (
            <div className="text-sm text-muted-foreground mt-2">
              No OPCS-4 codes found matching "{opcs4SearchTerm}". Try a different search term.
            </div>
          )}
        </TabsContent>
      </Tabs>

      {value.length > 0 && (
        <div className="space-y-4">
          {/* ICD-10 Codes Section */}
          {icd10SelectedCodes.length > 0 && (
            <div className="border rounded-md p-3">
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                ICD-10 Codes
              </h3>
              <ScrollArea className={icd10SelectedCodes.length > 3 ? "h-[150px]" : "max-h-full"}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="icd10-codes">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                        {icd10SelectedCodes.map((code, index) => (
                          <Draggable
                            key={`icd10-${code.code}`}
                            draggableId={`icd10-${code.code}`}
                            index={index}
                            isDragDisabled={disabled}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`p-2 bg-blue-50 border border-blue-100 rounded-md ${
                                  disabled ? "opacity-70" : ""
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-blue-800">{code.code}</span>
                                </div>
                                <p className="text-sm text-blue-600 truncate">{code.description}</p>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </ScrollArea>
            </div>
          )}

          {/* OPCS-4 Codes Section */}
          {opcs4SelectedCodes.length > 0 && (
            <div className="border rounded-md p-3">
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                OPCS-4 Codes
              </h3>
              <ScrollArea className={opcs4SelectedCodes.length > 3 ? "h-[150px]" : "max-h-full"}>
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="opcs4-codes">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                        {opcs4SelectedCodes.map((code, _index) => (
                          <Draggable
                            key={`opcs4-${code.code}`}
                            draggableId={`opcs4-${code.code}`}
                            index={value.findIndex((c) => c.code === code.code)}
                            isDragDisabled={disabled}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`p-2 bg-green-50 border border-green-100 rounded-md ${
                                  disabled ? "opacity-70" : ""
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-green-800">{code.code}</span>
                                </div>
                                <p className="text-sm text-green-600 truncate">{code.description}</p>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </ScrollArea>
            </div>
          )}

          {/* Show message when no codes are selected */}
          {value.length === 0 && (
            <div className="text-center p-4 text-muted-foreground border rounded-md">
              No clinical codes selected yet
            </div>
          )}
        </div>
      )}
    </div>
  );
}
